/**
 * AdminLayout - Sprint 31.7, Enhanced Sprint 31.11 Phase 2
 * Provides the admin area layout matching mockup-1 structure.
 * Left: sub-admin-sidebar with grouped navigation sections
 * Right: main content area (children)
 * 
 * Sprint 31.11 Phase 2: Role-based navigation split
 * - Platform Admin: Infrastructure management (tenants, OIDC, backups, health, metrics)
 * - Tenant Admin: Operational management (users, certs, domains, integrations)
 * 
 * Navigation Structure:
 * Platform Admin sections:
 * - Platform: overview, tenants, platform metrics
 * - Identity Providers: OIDC providers (global config)
 * - System Operations: backups, queue, health
 * - System Config: environment, security, rate limits, email
 * 
 * Tenant Admin sections:
 * - Identity: overview, users, roles, groups, permission-sets
 * - PKI: profiles, domains
 * - Integrations: SAML/LDAP/webhooks, audit-export
 * - Operations: rotations, ssh-policies, preauth
 * - Auditing & Activity: activity, audit, history, api-tokens
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

interface NavGroup {
  label: string;
  items: NavItem[];
  platformOnly?: boolean;  // Sprint 31.11: Platform admin only
  tenantOnly?: boolean;    // Sprint 31.11: Tenant admin only
}

interface NavItem {
  id: string;
  path: string;
  label: string;
  permission?: string;
  requireOrgTenant?: boolean;  // Sprint 34.15: Only show for organization-type tenants
}

// Sprint 31.11 Phase 2: Platform Admin Navigation
// Infrastructure management - no tenant data access
// Sprint 31.11 Phase 4: Routes moved under /manage/platform/* hierarchy
const platformAdminGroups: NavGroup[] = [
  {
    label: 'Platform',
    platformOnly: true,
    items: [
      { id: 'overview', path: '/manage/overview', label: 'Overview' },
      { id: 'tenants', path: '/manage/platform/tenants', label: 'Tenants', permission: 'manage_tenants' },
      { id: 'organizations', path: '/manage/platform/organizations', label: 'Organizations', permission: 'manage_organization' },  // Sprint 34.14
      { id: 'metrics', path: '/manage/platform/metrics', label: 'Platform Metrics', permission: 'view_health' },
    ],
  },
  {
    label: 'Identity Providers',
    platformOnly: true,
    items: [
      { id: 'oidc-providers', path: '/manage/platform/oidc-providers', label: 'OIDC Providers', permission: 'manage_integrations' },
    ],
  },
  {
    label: 'System Operations',
    platformOnly: true,
    items: [
      { id: 'backups', path: '/manage/platform/backups', label: 'Backups', permission: 'manage_backups' },
      { id: 'queue', path: '/manage/platform/queue', label: 'Queue', permission: 'view_jobs' },
      { id: 'health', path: '/manage/platform/health', label: 'Health', permission: 'view_health' },
    ],
  },
  {
    label: 'System Config',
    platformOnly: true,
    items: [
      { id: 'environment', path: '/manage/platform/environment', label: 'Environment', permission: 'view_system_status' },
      { id: 'security', path: '/manage/platform/security', label: 'Security', permission: 'view_system_status' },
      { id: 'rate-limits', path: '/manage/platform/rate-limits', label: 'Rate Limits', permission: 'view_system_status' },
      { id: 'email', path: '/manage/platform/email', label: 'Email Config', permission: 'view_system_status' },
      { id: 'billing', path: '/manage/platform/billing', label: 'Billing', permission: 'billing_read' },
      { id: 'validity-ceilings', path: '/manage/platform/validity-ceilings', label: 'Validity Ceilings', permission: 'view_system_status' },  // Sprint 11.1: SC-081v3
    ],
  },
];

// Sprint 31.11 Phase 2: Tenant Admin Navigation
// Operational management - scoped to current tenant
const tenantAdminGroups: NavGroup[] = [
  {
    label: 'Identity',
    tenantOnly: true,
    items: [
      { id: 'overview', path: '/manage/overview', label: 'Overview' },
      { id: 'users', path: '/manage/users', label: 'Users', permission: 'manage_users' },
      { id: 'organizations', path: '/manage/organizations', label: 'Organizations', permission: 'manage_organization', requireOrgTenant: true },  // Sprint 34.15: For org-type tenants
      { id: 'roles', path: '/manage/roles', label: 'Roles', permission: 'manage_roles' },
      { id: 'groups', path: '/manage/groups', label: 'Groups', permission: 'manage_groups' },
      { id: 'permission-sets', path: '/manage/permission-sets', label: 'Permission Sets', permission: 'manage_roles' },
    ],
  },
  {
    label: 'PKI',
    tenantOnly: true,
    items: [
      { id: 'profiles', path: '/manage/profiles', label: 'Profiles', permission: 'manage_profiles' },
      { id: 'domains', path: '/manage/domains', label: 'Domains', permission: 'manage_domains' },
      { id: 'pki-settings', path: '/manage/pki-settings', label: 'PKI Settings', permission: 'manage_tenant_ca' },  // Sprint 43: Chain baseline, OCSP HTTP
    ],
  },
  {
    label: 'Integrations',
    tenantOnly: true,
    items: [
      // Sprint 34.15: Identity Providers removed - now a category in Integrations wizard
      { id: 'integrations', path: '/manage/integrations', label: 'Integrations', permission: 'manage_integrations' },
      { id: 'audit-export', path: '/manage/export-audit', label: 'Audit Export', permission: 'view_audit' },
    ],
  },
  {
    label: 'Operations',
    tenantOnly: true,
    items: [
      { id: 'rotations', path: '/manage/rotations', label: 'Rotations', permission: 'manage_rotations' },
      { id: 'ssh-policies', path: '/manage/ssh-policies', label: 'SSH Policies', permission: 'manage_ssh_policies' },
      // Sprint 28: SSH Hosts moved to /pki/ssh-hosts (main nav)
      // Sprint 11.2 (S11.2.11): ACME Pre-Auth deprecated — redirects to /pki/eab
      // Sprint 11.2: EAB Credentials promoted to main sidebar under PKI Management (/pki/eab)
    ],
  },
  {
    label: 'Security',
    tenantOnly: true,
    items: [
      { id: 'key-management', path: '/manage/key-management', label: 'Key Management', permission: 'manage_tenant_ca' },
      // S48d.4/S48d.5: 'Two-Factor Auth' left this menu for the user's own Profile page, and
      // tenant Authentication policy took its place. The old entry was self-service MFA
      // (every endpoint it called was /mfa/* scoped to the CALLING user) sitting in an admin
      // menu, which read as a control an admin applies to a tenancy and invited looking for a
      // tenant-wide MFA policy that does not exist. This entry IS tenant policy, so unlike
      // its predecessor it carries a permission gate.
      { id: 'authentication', path: '/manage/authentication', label: 'Authentication', permission: 'manage_tenant_settings' },
      { id: 'billing', path: '/manage/billing', label: 'Billing & Plans', permission: 'billing_read' },
    ],
  },
  {
    label: 'Auditing & Activity',
    tenantOnly: true,
    items: [
      { id: 'activity', path: '/manage/activity', label: 'Activity', permission: 'view_audit' },
      { id: 'audit', path: '/manage/audit', label: 'Audit', permission: 'view_audit' },
      { id: 'history', path: '/manage/history', label: 'History', permission: 'view_audit' },
      { id: 'api-tokens', path: '/manage/tokens', label: 'API Tokens', permission: 'manage_tokens' },
      { id: 'compliance', path: '/manage/compliance', label: 'Compliance', permission: 'view_compliance' },
    ],
  },
];

export function AdminLayout(): React.JSX.Element {
  const location = useLocation();
  const { hasPermission, session } = useAuthStore();
  
  // Sprint 31.11 Phase 2: Determine if user is platform admin
  const isPlatformAdmin = session?.isPlatformAdmin ?? false;
  
  // Sprint 34.15: Determine if tenant is organization type
  const isOrgTenant = session?.accountType === 'organization';

  // Sprint 31.11 Phase 2: Select appropriate navigation based on role
  const adminGroups = isPlatformAdmin ? platformAdminGroups : tenantAdminGroups;

  // Filter groups based on permissions
  const visibleGroups = adminGroups
    .map((group) => {
      // Filter items based on permissions
      const visibleItems = group.items.filter((item) => {
        // Sprint 34.15: Check organization-type tenant requirement
        if (item.requireOrgTenant && !isOrgTenant) return false;
        // Check permission
        return !item.permission || hasPermission(item.permission);
      });

      if (visibleItems.length === 0) return null;

      return { ...group, items: visibleItems };
    })
    .filter((g): g is NavGroup => g !== null);

  return (
    <div className="admin-layout">
      {/* Sub-admin sidebar */}
      <nav className="sub-admin-sidebar" aria-label="Administration sections">
        {/* Sprint 31.11 Phase 2: Role badge */}
        <div 
          className="admin-role-badge"
          style={{
            padding: '12px 16px',
            marginBottom: '8px',
            borderBottom: '1px solid var(--border-color)',
            background: isPlatformAdmin ? 'var(--color-primary-faint)' : 'var(--bg-secondary)',
          }}
        >
          <div style={{
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '4px',
          }}>
            Admin Context
          </div>
          <div style={{
            fontSize: '13px',
            fontWeight: 500,
            color: isPlatformAdmin ? 'var(--color-primary)' : 'var(--text-primary)',
          }}>
            {/* S48.7: this was a BINARY -- everyone who was not a platform admin was labelled
                "Tenant Administrator", including an Issuance and Revocation Operator, who is
                not an administrator of anything. The badge asserted a role the viewer did not
                hold, in the one place a user looks to find out what they are.

                The session already carries the real role name and the top bar renders it
                correctly, so the correct value was in hand the whole time and this line simply
                did not ask for it. Falls back to a neutral label rather than inventing a
                third guess when the session has no role name. */}
            {isPlatformAdmin
              ? 'Platform Administrator'
              : session?.roleName || 'Signed in'}
          </div>
          {session && (
            <div style={{
              marginTop: '4px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
            }}>
              {isPlatformAdmin ? 'Infrastructure Management' : session.tenantName}
            </div>
          )}
        </div>

        {visibleGroups.map((group) => (
          <div key={group.label} className="group">
            <div className="group-h">
              {group.label}
            </div>
            {group.items.map((item) => (
              <NavLink
                key={item.id}
                to={item.path}
                className={({ isActive }) =>
                  `subitem ${isActive ? 'active' : ''}`
                }
                aria-current={location.pathname === item.path ? 'page' : undefined}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Main content */}
      <div className="admin-content">
        <Outlet />
      </div>
    </div>
  );
}
